/**
 * A binding's standing policy: reminders, quiet hours, caps, spend alert,
 * language, test devices, and whether this is the account's default.
 *
 * Everything is visible to everyone and writable only by an administrator. An
 * author needs to know what the send will inherit before they choose. Hiding the
 * settings from them would leave them guessing at limits they cannot see.
 */

import React, { useEffect } from 'react';
import { Panel, Button, Select } from '@cloudsam/ui';

// An emptied number box is "not set", not zero. Sending zero would be read as a
// real cap of nothing, which stops all sending while looking like no cap at all.
function numberOrNull(raw) {
    const trimmed = String(raw).trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isNaN(n) ? null : n;
}

function Row({ label, hint, canAdmin, children }) {
    return (
        <div className="defaults-row">
            <div>
                <div className="defaults-label">
                    {label}
                    {!canAdmin && <span className="pill-admin">Admin</span>}
                </div>
                <div className="defaults-hint">{hint}</div>
            </div>
            <div className="defaults-controls">{children}</div>
        </div>
    );
}

export default function BindingDefaultsOverlay({
    binding = {},
    defaults = {},
    canAdmin,
    onClose,
    onSave,
    onField,
    saving,
    error,
    testRecipients = [],
    isDefaultBinding,
    onMakeDefault,
    reminderPrice = '$0.0087',
    platformCap = 1000,
}) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const set = (name) => (e) => onField && onField(name, e.target.value);
    const setNumber = (name) => (e) => onField && onField(name, numberOrNull(e.target.value));
    const cap = defaults.daily_volume_cap;
    const capWidth = `${Math.min(100, Math.round(((cap || 0) / platformCap) * 100))}%`;

    return (
        <div className="overlay-scrim" onClick={onClose}>
            <div className="overlay-panel overlay-panel--narrow" onClick={(e) => e.stopPropagation()}>
                <Panel
                    title={`Binding defaults · ${binding.display_label || ''}`}
                    count={canAdmin ? 'editable' : 'read-only'}
                    actions={<Button variant="ghost" iconOnly aria-label="Close" onClick={onClose}>✕</Button>}
                >
                    <div className="panel-note">
                        These are this binding's defaults. The send flow inherits every value below
                        and may tighten it for one send — never exceed it.
                        <div>
                            {canAdmin
                                ? 'You are an administrator: every default below is yours to change, and every change is audit-logged.'
                                : 'You are a survey author: these are read-only. Pause, Stop and Disable remain available because each action reduces sending.'}
                        </div>
                    </div>

                    <Row label="Reminder policy" hint="Each reminder is a chargeable message." canAdmin={canAdmin}>
                        <label>
                            <input
                                type="checkbox"
                                checked={!!defaults.reminders_enabled}
                                onChange={(e) => onField && onField('reminders_enabled', e.target.checked)}
                                disabled={!canAdmin}
                            />
                            Reminders on
                        </label>
                        <label>
                            Maximum
                            <input
                                className="input input--tiny"
                                type="number"
                                min="0"
                                max="10"
                                value={defaults.reminder_max ?? ''}
                                onChange={setNumber('reminder_max')}
                                disabled={!canAdmin}
                            />
                        </label>
                        <label>
                            Spacing
                            <Select
                                value={defaults.reminder_spacing_hours ?? 24}
                                onChange={setNumber('reminder_spacing_hours')}
                                disabled={!canAdmin}
                            >
                                <option value={24}>24 hours</option>
                                <option value={48}>48 hours</option>
                                <option value={72}>72 hours</option>
                            </Select>
                        </label>
                        <span className="tag tag--yellow">{reminderPrice} per reminder</span>
                    </Row>

                    <Row label="Quiet hours" hint="Narrowing is open to authors; removing is not." canAdmin={canAdmin}>
                        <input
                            className="input input--narrow"
                            type="time"
                            value={defaults.quiet_hours_start ?? ''}
                            onChange={set('quiet_hours_start')}
                            disabled={!canAdmin}
                            aria-label="Quiet hours start"
                        />
                        <span>to</span>
                        <input
                            className="input input--narrow"
                            type="time"
                            value={defaults.quiet_hours_end ?? ''}
                            onChange={set('quiet_hours_end')}
                            disabled={!canAdmin}
                            aria-label="Quiet hours end"
                        />
                        {/* The audience's timezone, not the sender's. A survey sent from
                            Texas to Madrid has to be quiet at night in Madrid. */}
                        <Select
                            value={defaults.quiet_hours_timezone ?? 'America/Chicago'}
                            onChange={set('quiet_hours_timezone')}
                            disabled={!canAdmin}
                            aria-label="Quiet hours timezone"
                        >
                            <option value="America/Chicago">America/Chicago</option>
                            <option value="America/New_York">America/New_York</option>
                            <option value="America/Los_Angeles">America/Los_Angeles</option>
                            <option value="Europe/Madrid">Europe/Madrid</option>
                        </Select>
                        <span className="muted">Sends and reminders queue until the window opens.</span>
                    </Row>

                    <Row label="Daily volume cap" hint="At or below the platform tier." canAdmin={canAdmin}>
                        <input
                            className="input input--narrow"
                            type="number"
                            min="1"
                            value={cap ?? ''}
                            onChange={setNumber('daily_volume_cap')}
                            disabled={!canAdmin}
                            aria-label="Daily volume cap"
                        />
                        <span>unique recipients per day</span>
                        <div className="stack">
                            <div className="cap-track"><div className="cap-fill" style={{ width: capWidth }} /></div>
                            <div className="cap-legend">
                                <span>your cap {cap ?? '—'}</span>
                                <span>platform allows {platformCap}</span>
                            </div>
                        </div>
                    </Row>

                    <Row label="Spend alert" hint="Metered bindings only." canAdmin={canAdmin}>
                        <span>Notify at</span>
                        <input
                            className="input input--narrow"
                            type="number"
                            min="0"
                            value={defaults.spend_alert_threshold ?? ''}
                            onChange={setNumber('spend_alert_threshold')}
                            disabled={!canAdmin}
                            aria-label="Spend alert threshold"
                        />
                        <span>per month</span>
                    </Row>

                    <Row label="Language default" hint="When a recipient has none recorded." canAdmin={canAdmin}>
                        <Select
                            value={defaults.default_language ?? 'en_US'}
                            onChange={set('default_language')}
                            disabled={!canAdmin}
                            aria-label="Default language"
                        >
                            <option value="en_US">English (en_US)</option>
                            <option value="es">Spanish (es)</option>
                        </Select>
                    </Row>

                    <Row label="Test recipients" hint="Anyone may send a test; only admins change this list." canAdmin={canAdmin}>
                        {testRecipients.length === 0 ? (
                            <span className="muted">None added yet.</span>
                        ) : (
                            testRecipients.map((t) => (
                                <span key={t.test_recipient_id} className="chip">{t.label} · {t.address}</span>
                            ))
                        )}
                        <Button variant="secondary" disabled={!canAdmin}>Add Recipient</Button>
                    </Row>

                    <Row label="Default binding" hint="Preselected in the send flow." canAdmin={canAdmin}>
                        <label>
                            <input
                                type="checkbox"
                                checked={!!isDefaultBinding}
                                onChange={onMakeDefault}
                                disabled={!canAdmin}
                            />
                            Make this the default {binding.channel} binding
                        </label>
                    </Row>

                    {error && <div className="panel-error">{error}</div>}

                    <div className="panel-footer">
                        <span>All changes are audit-logged, whoever makes them.</span>
                        <div className="panel-footer-end">
                            <Button variant="secondary" onClick={onClose}>Close</Button>
                            <Button variant="primary" disabled={!canAdmin || saving} onClick={onSave}>
                                Save Defaults
                            </Button>
                        </div>
                    </div>
                </Panel>
            </div>
        </div>
    );
}
